import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { ROLE_LABELS, ROLE_COLORS, ROLES } from '../utils/roles.helpers';
import { confirmDialog } from '../utils/confirm';

const ALL_ROLES = Object.entries(ROLE_LABELS)
  .filter(([r]) => r !== ROLES.SUPER_ADMIN)
  .map(([value, label]) => ({ value, label }));

export default function GestionRoles() {
  const [users, setUsers]   = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError]   = useState('');
  const [flash, setFlash]   = useState('');
  const [modal, setModal]   = useState(null);

  function load() {
    api.get('/users').then(setUsers).catch((e) => setError(e.message));
  }

  useEffect(load, []);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return !q || `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(q);
  });

  async function toggleStatus(user) {
    const newStatus = user.status === 'ACTIF' ? 'INACTIF' : 'ACTIF';
    const msg = newStatus === 'INACTIF' ? `Désactiver ${user.firstName} ${user.lastName} ?` : `Réactiver ${user.firstName} ${user.lastName} ?`;
    if (!(await confirmDialog({ title: 'Modifier le statut', message: msg, danger: newStatus === 'INACTIF' }))) return;
    try {
      await api.patch(`/users/${user.id}`, { status: newStatus });
      setFlash(`Compte ${newStatus === 'ACTIF' ? 'réactivé' : 'désactivé'} ✓`);
      setTimeout(() => setFlash(''), 3000);
      load();
    } catch (e) { setError(e.message); }
  }

  async function resetPassword(user) {
    if (!(await confirmDialog({ title: 'Réinitialiser le mot de passe', message: `Réinitialiser le mot de passe de ${user.firstName} ${user.lastName} à "Schoolizy@2026!" ?`, danger: false }))) return;
    try {
      await api.patch(`/users/${user.id}/reset-password`, {});
      setFlash('Mot de passe réinitialisé ✓');
      setTimeout(() => setFlash(''), 3000);
    } catch (e) { setError(e.message); }
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h1 style={{ fontSize: '1.2rem', fontWeight: 600 }}>Gestion des rôles</h1>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            Gérez les accès et rôles de chaque utilisateur de l'établissement.
          </p>
        </div>
        <button className="btn btn-sm" onClick={() => setModal({ mode: 'create' })}>+ Nouvel utilisateur</button>
      </div>

      {error && <div className="form-error">{error}</div>}
      {flash && <div className="receipt-banner">{flash}</div>}

      {/* Stats par rôle */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {ALL_ROLES.map((r) => {
          const count = users.filter((u) => (u.roles || []).includes(r.value)).length;
          if (count === 0) return null;
          return (
            <span key={r.value} style={{ padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 500, background: ROLE_COLORS[r.value] + '18', color: ROLE_COLORS[r.value], border: `1px solid ${ROLE_COLORS[r.value]}30` }}>
              {r.label} ({count})
            </span>
          );
        })}
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Utilisateurs ({filtered.length})</h2>
          <div style={{ position: 'relative', width: 240 }}>
            <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', fontSize: 14 }}>🔍</span>
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom, email…" style={{ paddingLeft: 30, height: 34, width: '100%' }} />
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {filtered.length === 0 ? <div className="empty">Aucun utilisateur.</div> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>Email</th>
                    <th>Rôles</th>
                    <th>Statut</th>
                    <th>Dernière connexion</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                            background: ROLE_COLORS[(u.roles || [])[0]] || 'var(--primary)',
                            color: '#fff', display: 'grid', placeItems: 'center',
                            fontWeight: 600, fontSize: 13,
                          }}>
                            {(u.firstName?.[0] || '?')}{(u.lastName?.[0] || '')}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{u.firstName} {u.lastName}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{u.phone || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>{u.email}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(u.roles || []).map((r) => (
                            <span key={r} style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 500, background: ROLE_COLORS[r] + '20', color: ROLE_COLORS[r] }}>
                              {ROLE_LABELS[r] || r}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${u.status === 'ACTIF' ? 'green' : 'red'}`}>
                          {u.status === 'ACTIF' ? '● Actif' : '● Inactif'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                        {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Jamais'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn-ghost btn-sm" onClick={() => setModal({ mode: 'edit', user: u })}>
                            Modifier
                          </button>
                          <button className="btn-ghost btn-sm" onClick={() => resetPassword(u)} title="Réinitialiser le mot de passe">
                            🔑
                          </button>
                          <button className={`btn-ghost btn-sm`}
                            style={{ color: u.status === 'ACTIF' ? 'var(--danger)' : 'var(--success)' }}
                            onClick={() => toggleStatus(u)}>
                            {u.status === 'ACTIF' ? 'Désactiver' : 'Réactiver'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <UserModal
          initial={modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); setFlash('Utilisateur enregistré ✓'); setTimeout(() => setFlash(''), 3000); }}
        />
      )}
    </>
  );
}

function UserModal({ initial, onClose, onSaved }) {
  const isEdit = initial.mode === 'edit';
  const u = initial.user || {};
  const [f, setF] = useState({
    firstName: u.firstName || '',
    lastName:  u.lastName  || '',
    email:     u.email     || '',
    phone:     u.phone     || '',
    roles:     u.roles     || [],
    password:  '',
    sessionTimeout: u.sessionTimeout ?? 5,
  });
  const [error, setError]   = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  function toggleRole(role) {
    setF((p) => ({
      ...p,
      roles: p.roles.includes(role)
        ? p.roles.filter((r) => r !== role)
        : [...p.roles, role],
    }));
  }

  async function save() {
    if (!f.firstName || !f.lastName || !f.email) { setError('Prénom, nom et email sont obligatoires.'); return; }
    if (f.roles.length === 0) { setError('Sélectionnez au moins un rôle.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { firstName: f.firstName, lastName: f.lastName, email: f.email, phone: f.phone || undefined, roles: f.roles, sessionTimeout: parseInt(f.sessionTimeout) || 5 };
      if (!isEdit) payload.password = f.password || 'Schoolizy@2026!';
      if (isEdit) {
        await api.patch(`/users/${u.id}`, payload);
      } else {
        await api.post('/users', payload);
      }
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginBottom: 16 }}>{isEdit ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</h2>
        {error && <div className="form-error">{error}</div>}

        <div className="two-col">
          <div className="field"><label>Prénom *</label><input value={f.firstName} onChange={set('firstName')} /></div>
          <div className="field"><label>Nom *</label><input value={f.lastName} onChange={set('lastName')} /></div>
        </div>
        <div className="two-col">
          <div className="field"><label>Email *</label><input type="email" value={f.email} onChange={set('email')} /></div>
          <div className="field"><label>Téléphone</label><input value={f.phone} onChange={set('phone')} /></div>
        </div>
        {!isEdit && (
          <div className="field">
            <label>Mot de passe <small style={{ color: 'var(--muted)' }}>(défaut : Schoolizy@2026!)</small></label>
            <input type="password" value={f.password} onChange={set('password')} placeholder="Laisser vide pour le mot de passe par défaut" />
          </div>
        )}

        <div className="field">
          <label>Délai d'inactivité (minutes)</label>
          <input type="number" min="1" max="480" value={f.sessionTimeout}
            onChange={(e) => setF(p => ({ ...p, sessionTimeout: e.target.value }))}
            style={{ width: 120 }} />
          <small style={{ color: 'var(--muted)' }}>Déconnexion automatique après X minutes sans activité</small>
        </div>
        <div className="fieldset-title">Rôles</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
          {ALL_ROLES.map((r) => {
            const active = f.roles.includes(r.value);
            return (
              <button key={r.value} type="button"
                onClick={() => toggleRole(r.value)}
                style={{
                  padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
                  border: `1.5px solid ${active ? ROLE_COLORS[r.value] : 'var(--line)'}`,
                  background: active ? ROLE_COLORS[r.value] + '15' : 'var(--surface)',
                  color: active ? ROLE_COLORS[r.value] : 'var(--muted)',
                  fontWeight: active ? 600 : 400, fontSize: 12, textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: active ? ROLE_COLORS[r.value] : 'var(--line)', flexShrink: 0 }} />
                {r.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>
            {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer'}
          </button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}
